import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Select, Space } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { DATE_AND_TIME, PortActionTypeLabels, TimeZoneFormat } from '@shared/utils/constants';
import { getUtcOffset, toMoment } from '@shared/utils/date';
import { capitalize } from '@shared/utils/string';
import { formatOffset } from '@shared/utils/timezones';
import { sortPortCallActivities } from '@shared/utils/sortPortCallActivities';
import { PortActivityProps } from '@api/models/port-activity';
import { PortCallActivityProps } from '@api/models/port-call-activity';
import Card from '@client/components/Card/Card';
import Button from '@client/components/Button';
import Table from '@client/components/Table/Table';
import { formatDate } from '@client/utils/formatDate';
import { useAuth } from '@client/lib/auth';
import { createPortCallActivity, deletePortCallActivity, updatePortCallActivity } from '@client/lib/api';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import DatePicker from '@client/components/DatePicker';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { useVoyage } from '../../VoyageProvider/VoyageProvider';

const PortCallActivities = ({
	onRefresh,
	selectedPortCall,
	vesselId,
}: {
	onRefresh: () => void;
	selectedPortCall: ItineraryPortCallDto;
	vesselId: number;
}) => {
	const {
		cargos,
	} = useVoyage();

	const [form] = Form.useForm();
	const [showActivityDrawer, setShowActivityDrawer] = useState(false);
	const [displayOffMin, setDisplayOffMin] = useState<number>(0);
	const [editingActivityId, setEditingActivityId] = useState<number | null>(null);
	const auth = useAuth();

	const portActivities = useMemo(() => {
		if (!auth.userInfo) {
			return [];
		}

		return auth.userInfo.portActivities;
	}, [auth]);

	const onDelete = async (id: number) => {
		await deletePortCallActivity(vesselId, selectedPortCall.id, id);
		await onRefresh();
	};

	const onClose = () => {
		setEditingActivityId(null);
		setShowActivityDrawer(false);
		form.resetFields();
	};

	const onSave = async () => {
		const values = form.getFieldsValue(true);

		const date = values.date;

		const localMoment = toMoment(date).clone();
		const offMin = getUtcOffset(
			selectedPortCall.port,
			localMoment.toDate(),
		) ?? 0;

		const dateToStore = localMoment.clone().utcOffset(offMin, true);

		if (editingActivityId != null) {
			if (values.event != null) {
				try {
					await updatePortCallActivity(
						vesselId,
						selectedPortCall.id,
						editingActivityId,
						values.event,
						dateToStore,
						selectedPortCall.port.id
					);
					await onRefresh();
					showSuccessNotification('Activity updated successfully');
					onClose();
				} catch (e) {
					showErrorNotification('Could not update activity', e as Error);
				}
			} else {
				showErrorNotification('Event cannot be blank');
			}
		} else {
			try {
				await createPortCallActivity(
					vesselId,
					selectedPortCall.id,
					values.event,
					dateToStore,
					selectedPortCall.port.id
				);
				await onRefresh();
				showSuccessNotification('Activity created successfully');
				onClose();
			} catch (e) {
				showErrorNotification('Could not create activity', e as Error);
			}
		}
	};

	const sortedActivities = useMemo(() => {
		if (portActivities == null) {
			return [];
		}

		return sortPortCallActivities(selectedPortCall.PortCallActivities, portActivities);
	}, [selectedPortCall, portActivities]);

	const editingActivity = useMemo(() => {
		const activity = selectedPortCall.PortCallActivities.find((a) => a.id === editingActivityId);

		return activity;
	}, [selectedPortCall, editingActivityId]);

	const selectedDate = useMemo(
		() => form.getFieldValue('date') ?? editingActivity?.date, 
		[form.getFieldsValue(['date']), editingActivity]
	);

	useEffect(() => {
		if (!editingActivity) {
			form.resetFields();
			setDisplayOffMin(0);

			return;
		}

		const storedUtc = editingActivity.date ? toMoment(editingActivity.date) : null;

		const offMin = selectedPortCall ?
			(getUtcOffset(selectedPortCall.port, storedUtc?.toDate()) ?? 0) : 0;

		const portLocal = storedUtc?.clone().utcOffset(offMin);

		form.setFieldsValue({
			date: portLocal,
			event: editingActivity.event,
			cargoId: editingActivity.cargoId,
		});
		setDisplayOffMin(offMin);
	}, [editingActivity?.id, selectedPortCall, form]);

	return (
		<>
			<Drawer
				width={500}
				title={`${editingActivity ? 'Update' : 'Create'} activity in ${selectedPortCall.port.name}`}
				open={showActivityDrawer || editingActivity != null}
				onClose={onClose}
			>
				<Form
					layout="vertical"
					form={form}
				>
					<Form.Item required label="Activity" name="event">
						<Select
							showSearch
							placeholder="Select activity"
							options={
								portActivities.map((a: PortActivityProps) => ({
									label: a.name,
									value: a.name,
								}))
							}
						/>
					</Form.Item>
					<Form.Item required label="Date" name="date">
						<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
							<DatePicker
								value={selectedDate}
								time
								onChange={(val) => {
									if (!val || Array.isArray(val)) {
										return;
									}

									form.setFieldsValue({ date: val });

									if (selectedPortCall) {
										setDisplayOffMin(
											getUtcOffset(
												selectedPortCall.port,
												val,
											) ?? 0,
										);
									}
								}}
								showTimezone={false}
							/>
							<span style={{ opacity: 0.7 }}>{formatOffset(displayOffMin)}</span>
						</div>
					</Form.Item>
					<Form.Item label="Cargo (optional)" name="cargoId">
						<Select
							showSearch
							placeholder="Select cargo"
							options={(cargos ?? []).map((c) => ({
								label: c.type,
								value: c.id,
							}))}
						/>
					</Form.Item>
					<Button
						icon={(<SaveOutlined />)}
						type="primary"
						onClick={onSave}
					>
						Save
					</Button>
				</Form>
			</Drawer>
			<Card
				slim
				title="Activities"
				extra={(
					<Button
						onClick={() => setShowActivityDrawer(true)}
						icon={(<PlusOutlined />)}
						type="primary"
					>
						New Port Activity
					</Button>
				)}
			>
				<Table<PortCallActivityProps>
					pagination={false}
					dataSource={sortedActivities}
					locale={{ emptyText: 'No activites registrered' }}
					size="small"
					rowKey="id"
					columns={[
						{
							title: 'Activity',
							dataIndex: 'event',
							render: (a) => {
								const asNumber = Number(a);
								const isValid = Number.isInteger(asNumber);

								if (isValid) {
									const event = portActivities.find((act) => act.id === asNumber)?.name;

									return event;
								}

								const event = PortActionTypeLabels[a as keyof typeof PortActionTypeLabels] ?? capitalize(a);

								return event;
							},
						},
						{
							title: 'Date',
							dataIndex: 'date',
							render: (d, row) => {
								if (!d) {
									return 'N/A';
								}

								const offMin = getUtcOffset((row as any)?.port, new Date(d)) ?? 0;
								const m = toMoment(d).utc().utcOffset(offMin);

								return formatDate(m, DATE_AND_TIME, true, TimeZoneFormat.LocalTime);
							},
						},
						{
							title: 'Cargo',
							dataIndex: 'cargoId',
							render: (cargoId) => {
								if (cargoId == null) {
									return ' - ';
								}

								const cargo = (cargos ?? []).find((cg) => cg.id === cargoId);

								if (cargo == null) {
									return ' - ';
								}

								return cargo.type;
							},
						},
						{
							title: '',
							dataIndex: 'id',
							align: 'right' as const,
							render: (id) => id > 0 && (
								<Space>
									<Button
										onClick={() => {
											setEditingActivityId(id);
										}}
										type="link"
										icon={(<EditOutlined />)}
									/>
									<Button
										onClick={() => onDelete(id)}
										danger
										confirmTitle="Are you sure you want to delete this activity"
										type="link"
										icon={(<DeleteOutlined />)}
									/>
								</Space>
							),
						},
					]}
				/>
			</Card>
		</>
	);
};

export default PortCallActivities;
